import React, { useState, useEffect } from 'react';
import { history as historyApi } from '../../api/client';
import { Clock, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function HistoryPage({ activeBrandId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const params = { days };
        if (activeBrandId) params.brand_id = activeBrandId;
        const result = await historyApi.get(params);
        setData(result.history || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadData();
  }, [activeBrandId, days]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Analytics</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Position History</h1>
        </div>
        <div className="flex border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-sm ${days === d ? 'bg-stone-900 text-white dark:bg-white dark:text-stone-900' : 'text-stone-600 dark:text-stone-400'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : data.length === 0 ? (
        <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-8 text-center text-stone-500">No history data available yet.</div>
      ) : (
        <>
          <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Overall Visibility Trend</h2>
            <div className="h-80" style={{ minHeight: '320px', minWidth: '200px' }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={320}>
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1c1917" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#1c1917" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="recorded_at" stroke="#a8a29e" fontSize={12} tickLine={false} axisLine={false}
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                  <YAxis stroke="#a8a29e" fontSize={12} tickLine={false} axisLine={false} domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="score" stroke="#1c1917" fill="url(#histGrad)" strokeWidth={2} name="Overall" />
                  <Area type="monotone" dataKey="chatgpt" stroke="#10b981" fill="none" strokeWidth={1} strokeDasharray="3 3" name="ChatGPT" />
                  <Area type="monotone" dataKey="perplexity" stroke="#8b5cf6" fill="none" strokeWidth={1} strokeDasharray="3 3" name="Perplexity" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <div className="grid grid-cols-7 gap-4 px-4 py-3 bg-stone-50 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-700 text-xs font-medium text-stone-500 uppercase">
              <div>Date</div><div className="text-center">Overall</div><div className="text-center">GPT</div>
              <div className="text-center">PPX</div><div className="text-center">Claude</div>
              <div className="text-center">Gemini</div><div className="text-center">GAI</div>
            </div>
            {data.slice(-10).reverse().map((item, i) => (
              <div key={i} className="grid grid-cols-7 gap-4 px-4 py-3 border-b border-stone-100 dark:border-stone-700 text-sm">
                <div className="text-stone-600 dark:text-stone-300">{new Date(item.recorded_at).toLocaleDateString()}</div>
                <div className="text-center font-medium text-stone-900 dark:text-white">{item.score}</div>
                <div className="text-center text-stone-600">{item.chatgpt ?? '-'}</div>
                <div className="text-center text-stone-600">{item.perplexity ?? '-'}</div>
                <div className="text-center text-stone-600">{item.claude ?? '-'}</div>
                <div className="text-center text-stone-600">{item.gemini ?? '-'}</div>
                <div className="text-center text-stone-600">{item.google_aio ?? '-'}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
